import React, { useState, useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { legendColor, legendSize } from 'd3-svg-legend';
import { scaleOrdinal } from 'd3-scale';
import data1 from './data';
import toolUrls from './toolUrls';
import './style.scss';
let data = [...data1];

const categoryData = [
  'React',
  'Next.js',
  'TypeScript',
  'Tailwind CSS',
  'shadcn/ui',
  'Vite',
  'Node.js',
  'GraphQL',
  'Vercel',
  'AWS',
  // 'OpenAI',
  'Cursor',
  'Vue',
  // 'GitHub Copilot',
];

const CATEGORY_TICK_MS = 500;

function CategoryTicker({ items }: { items: string[] }) {
  const [current, setCurrent] = useState(items[0]);
  const indexRef = useRef(0);
  const nextAtRef = useRef(performance.now() + CATEGORY_TICK_MS);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    indexRef.current = 0;
    setCurrent(items[0]);
    nextAtRef.current = performance.now() + CATEGORY_TICK_MS;

    const schedule = () => {
      const delay = Math.max(0, nextAtRef.current - performance.now());
      timerRef.current = window.setTimeout(() => {
        indexRef.current = (indexRef.current + 1) % items.length;
        setCurrent(items[indexRef.current]);
        nextAtRef.current += CATEGORY_TICK_MS;
        schedule();
      }, delay);
    };

    schedule();

    return () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    };
  }, [items]);

  return <span className='language-ticker'>{current}</span>;
}

function TechStack() {
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const tip = document.createElement('div');
    tip.id = 'bubble-tooltip';
    tip.className = 'bubble-tooltip';
    tip.setAttribute('role', 'tooltip');
    document.body.appendChild(tip);
    tooltipRef.current = tip;

    techStackDisplay();
    const onResize = () => techStackDisplay();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      tip.remove();
      tooltipRef.current = null;
    };
  }, []);

  function techStackDisplay() {
    (document as any).querySelector('#bubble-graph').innerHTML = '';
    let svg = d3.select('#bubble-graph');
    let width = document.body.clientWidth;
    let height = +svg.attr('height');
    let centerX = width * 0.5;
    let centerY = height * 0.5;
    let strength = 0.05;

    const priorityCategories = ['ai library', 'ai tooling'];
    const categories = Array.from(new Set(data.map((d: any) => d.cat)));
    const legendOrder = [
      ...priorityCategories.filter((c) => categories.includes(c)),
      ...categories.filter((c) => !priorityCategories.includes(c)).sort(),
    ];
    let scaleColor = scaleOrdinal(legendOrder, d3.schemeCategory10);

    // use pack to calculate radius of the circle
    let pack = d3.pack().size([width, height]).padding(1.5);

    let forceCollide = d3.forceCollide((d: any) => d.r + 1);

    // use the force
    let simulation = d3
      .forceSimulation()
      // .force('link', d3.forceLink().id(d => d.id))
      .force('charge', d3.forceManyBody())
      .force('collide', forceCollide)
      // .force('center', d3.forceCenter(centerX, centerY))
      .force('x', d3.forceX(centerX).strength(strength))
      .force('y', d3.forceY(centerY).strength(strength));

    // reduce number of circles on mobile screen due to slow computation
    if ('matchMedia' in window && window.matchMedia('(max-device-width: 767px)').matches) {
      data = data.filter((el) => {
        return el.value >= 50;
      });
    }

    let root = d3.hierarchy({ children: data }).sum((d: any) => d.value);

    // we use pack() to automatically calculate radius conveniently only
    // and get only the leaves
    let nodes = pack(root as d3.HierarchyNode<unknown>)
      .leaves()
      .map((node) => {
        // console.log('node:', node.x, (node.x - centerX) * 2);
        const data: any = node.data;
        return {
          x: centerX + (node.x - centerX) * 3, // magnify start position to have transition to center movement
          y: centerY + (node.y - centerY) * 3,
          r: 0, // for tweening
          radius: node.r, //original radius
          id: data.cat + '.' + data.name.replace(/\s/g, '-'),
          cat: data.cat,
          name: data.name,
          value: data.value,
          icon: data.icon,
          desc: data.desc,
          url: data.url || (toolUrls as Record<string, string>)[data.name],
        };
      });
    simulation.nodes(nodes).on('tick', ticked);

    svg.style('background-color', '#ecf0f1'); //#eee
    let node = svg.selectAll('.node').data(nodes).enter().append('g').attr('class', 'node');
    // .call(
    //   d3
    //     .drag()
    //     .on('start', () => {
    //       // let d = d3.select(this);
    //       // if (!event.active) simulation.alphaTarget(0.2).restart();
    //       // d.fx = event.x;
    //       // d.fy = event.y;
    //     })
    //     .on('drag', () => {
    //       // let d = d3.select(this);
    //       // d.fx = event.x;
    //       // d.fy = event.y;
    //     })
    //     .on('end', () => {
    //       // let d = d3.select(this);
    //       // if (!event.active) simulation.alphaTarget(0);
    //       // d.fx = null;
    //       // d.fy = null;
    //     })
    // );

    node
      .append('circle')
      .attr('id', (d) => d.id)
      .attr('r', 0)
      .style('fill', (d) => scaleColor(d.cat))
      .transition()
      .duration(2000)
      .ease(d3.easeElasticOut)
      .tween('circleIn', (d) => {
        let i = d3.interpolateNumber(0, d.radius);
        return (t) => {
          d.r = i(t);
          simulation.force('collide', forceCollide);
        };
      });

    node
      .append('clipPath')
      .attr('id', (d) => `clip-${d.id}`)
      .append('use')
      .attr('xlink:href', (d) => `#${d.id}`);

    const isImageIcon = (icon: string) => /\.(png|svg|jpe?g|gif|webp)$/i.test(icon);

    // display image as circle icon
    node
      .filter((d) => isImageIcon(String(d.icon)))
      .append('image')
      .classed('node-icon', true)
      .attr('clip-path', (d) => `url(#clip-${d.id})`)
      .attr('href', (d) => process.env.PUBLIC_URL + '/images/' + d.icon)
      .attr('x', (d) => -d.radius * 0.7)
      .attr('y', (d) => -d.radius * 0.7)
      .attr('height', (d) => d.radius * 2 * 0.7)
      .attr('width', (d) => d.radius * 2 * 0.7)
      .style('filter', (d) =>
        String(d.icon).endsWith('.svg') ? 'brightness(0) invert(1)' : null
      );

    // display text as circle icon
    node
      .filter((d) => !isImageIcon(String(d.icon)))
      .append('text')
      .classed('node-icon', true)
      .attr('clip-path', (d) => `url(#clip-${d.id})`)
      .selectAll('tspan')
      .data((d) => d.icon.split(';'))
      .enter()
      .append('tspan')
      .attr('x', 0)
      .attr('y', (d, i, nodes) => 13 + (i - nodes.length / 2 - 0.5) * 10)
      .text((d) => String(d));

    const positionTooltip = (event: MouseEvent) => {
      const tip = tooltipRef.current;
      if (!tip) return;
      tip.style.left = `${event.clientX + 14}px`;
      tip.style.top = `${event.clientY + 14}px`;
    };

    const showTooltip = (event: MouseEvent, d: any) => {
      const tip = tooltipRef.current;
      if (!tip) return;
      d.fx = d.x;
      d.fy = d.y;
      simulation.alphaTarget(0.05).restart();
      tip.innerHTML = `<strong>${d.name}</strong><span class="bubble-tooltip__cat">${d.cat}</span>`;
      tip.classList.add('visible');
      positionTooltip(event);
    };

    const hideTooltip = (_event: MouseEvent, d: any) => {
      const tip = tooltipRef.current;
      if (!tip) return;
      d.fx = null;
      d.fy = null;
      simulation.alphaTarget(0);
      tip.classList.remove('visible');
    };

    node.select('circle').style('pointer-events', 'all').style('cursor', 'pointer');

    node
      .selectAll('.node-icon, .circle-overlay')
      .style('pointer-events', 'none');

    node
      .on('mouseover', showTooltip)
      .on('mousemove', positionTooltip)
      .on('mouseout', hideTooltip);

    let legendOrdinal = (legendColor().scale(scaleColor) as any).shape('circle');
    // append category legend
    svg
      .append('g')
      .classed('legend-color', true)
      .attr('text-anchor', 'start')
      .attr('transform', 'translate(20,30)')
      .style('font-size', '12px')
      .call(legendOrdinal);

    let sizeScale = d3.scaleOrdinal().domain(['less use', 'more use']).range([5, 10]);

    // let legendS = legendSize().scale(sizeScale);
    // .shape('circle').shapePadding(10).labelAlign('end');
    let legendS = (legendSize().scale(sizeScale) as any).shape('circle').shapePadding(10).labelAlign('end');

    // append size legend
    svg
      .append('g')
      .classed('legend-size', true)
      .attr('text-anchor', 'start')
      .attr('transform', 'translate(150, 25)')
      .style('font-size', '12px')
      .call(legendS);


    node.on('click', (event, d) => {
      event.stopPropagation();
      if (d.url) {
        window.open(d.url, '_blank', 'noopener,noreferrer');
      }
    });

    function ticked() {
      node
        .attr('transform', (d) => `translate(${d.x},${d.y})`)
        .select('circle')
        .attr('r', (d) => d.r);
    }
  }
  return (
    <>
      <section className='section section-languages'>
        <h1>
          Effy's Technology Stack ❤️ <CategoryTicker items={categoryData} />
        </h1>

        <ul className='language-options'>
          {categoryData.map((d, i) => (
            <li key={i}>{d}</li>
          ))}
        </ul>
      </section>

      <svg id='bubble-graph' width='100%' height='700' fontFamily='sans-serif' fontSize='10' textAnchor='middle'></svg>
    </>
  );
}

export default TechStack;
